"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { FiDownload, FiRefreshCw } from "react-icons/fi";
import { DropdownMenuItem } from "@/components/ui/dropdown";
import { detectBrowser } from "@/lib/detect-browser";
import { isVideoUrl } from "@/lib/image-compression";
import { toast } from "@/lib/use-toast";
import { fetchAttachmentFile } from "@/api/auth/gcs";

// Full-size photos load a few at a time, so selecting 30 doesn't open 30
// downloads at once on an older iPad.
const FETCH_CONCURRENCY = 4;

// Save to Files copies the shared files after the user picks a folder, which
// can be well after share() resolves, so the page keeps them this long.
const KEEP_SHARED_FILES_MS = 5 * 60 * 1000;
export interface SaveToPhotosItem {
  id: string;
  gcsPath?: string;
  name?: string;
}

/**
 * Whether this device can save photos through the native share sheet. Only
 * iPhone and iPad get it: there the sheet offers "Save N Images" (Photos)
 * next to "Save to Files". Android downloads already show up in the gallery
 * app, and its share sheet only offers cloud uploads, so it keeps Download.
 */
export function canSaveToPhotos(): boolean {
  if (typeof navigator === "undefined" || !navigator.canShare) return false;
  if (detectBrowser(navigator.userAgent, navigator.maxTouchPoints) !== "ios") {
    return false;
  }
  const probe = new File([""], "probe.jpg", {
    type: "image/jpeg"
  });
  return navigator.canShare({
    files: [probe]
  });
}
export function isVideoItem(item: SaveToPhotosItem): boolean {
  // Either can carry the extension: some stored paths have none.
  return isVideoUrl(item.gcsPath ?? "") || isVideoUrl(item.name ?? "");
}
let sharedFiles: File[] = [];
let releaseSharedFiles: ReturnType<typeof setTimeout> | undefined;

// "Save to Files" hangs on Save when the folder already has a file with the
// same name, whether from an earlier save or another photo in this share
// (camera uploads are all called "image"), so every file gets its own random
// suffix: image.jpeg -> image-k3x9.jpeg.
function withUniqueName(file: File): File {
  const suffix = Math.random().toString(36).slice(2, 6);
  const dot = file.name.lastIndexOf(".");
  const name = dot > 0 ? `${file.name.slice(0, dot)}-${suffix}${file.name.slice(dot)}` : `${file.name}-${suffix}`;
  return new File([file], name, {
    type: file.type
  });
}

/**
 * Opens the share sheet with photos that are already loaded. Safari only
 * opens it straight after a tap, so call this from the click handler before
 * anything is awaited. Files only: adding a title or text makes iOS drop
 * "Save Image". Resolves once the sheet closes; closing it without choosing
 * anything is not an error, and any other failure shows one.
 */
export function sharePhotoFiles(files: File[]): Promise<boolean> {
  clearTimeout(releaseSharedFiles);
  sharedFiles = files.map(withUniqueName);
  let shared: Promise<void>;
  try {
    shared = navigator.share({
      files: sharedFiles
    });
  } catch (error) {
    shared = Promise.reject(error);
  }
  return shared.then(() => true, error => {
    if (error instanceof DOMException && error.name === "AbortError") {
      return false;
    }
    console.error("Failed to open share sheet:", error);
    toast({
      title: "Couldn't open the save options",
      description: "Try again, or download from a computer.",
      variant: "error"
    });
    return false;
  }).finally(() => {
    releaseSharedFiles = setTimeout(() => {
      sharedFiles = [];
    }, KEEP_SHARED_FILES_MS);
  });
}
let activeLoads = 0;
const waitingLoads: (() => void)[] = [];
async function withLoadSlot<T>(load: () => Promise<T>): Promise<T> {
  if (activeLoads >= FETCH_CONCURRENCY) {
    await new Promise<void>(resolve => waitingLoads.push(resolve));
  }
  activeLoads++;
  try {
    return await load();
  } finally {
    activeLoads--;
    waitingLoads.shift()?.();
  }
}

/**
 * Loads a photo as a shareable File; null when it can't be loaded or was
 * cancelled. Selection loads run FETCH_CONCURRENCY at a time. `priority`
 * (one photo's menu) skips that queue, so it isn't stuck behind a big
 * selection.
 */
export function loadPhotoFile(photo: SaveToPhotosItem, {
  priority = false,
  signal
}: {
  priority?: boolean;
  signal?: AbortSignal;
} = {}): Promise<File | null> {
  const {
    gcsPath
  } = photo;
  if (!gcsPath) return Promise.resolve(null);
  // A load cancelled while it waited for a slot never starts.
  const load = async () => signal?.aborted ? null : fetchAttachmentFile(gcsPath, photo.name || `photo-${photo.id.slice(0, 8)}`, {
    signal
  });
  return (priority ? load() : withLoadSlot(load)).catch(error => {
    if (!signal?.aborted) {
      console.error(`Failed to load photo ${photo.id}:`, error);
    }
    return null;
  });
}

// One photo's menu loads the photo when it opens. Reopening the menu, or
// opening it again from the full-size viewer, reuses the same load for a
// couple of minutes, after which it's dropped from memory. Only a few are
// kept, since each is a full-size photo, and only the newest menu's load
// runs: opening another menu cancels it.
const MENU_CACHE_SIZE = 3;
const MENU_CACHE_MS = 2 * 60 * 1000;
const menuLoads = new Map<string, {
  file: Promise<File | null>;
  loaded?: File;
  expire: ReturnType<typeof setTimeout>;
}>();
function dropMenuLoad(key: string) {
  clearTimeout(menuLoads.get(key)?.expire);
  menuLoads.delete(key);
}
let inFlightMenuLoad: {
  key: string;
  controller: AbortController;
} | null = null;

// The name is part of the key so a renamed photo is shared under its new name
const menuKey = (photo: SaveToPhotosItem) => `${photo.gcsPath}\n${photo.name ?? ""}`;
function loadMenuPhoto(photo: SaveToPhotosItem & {
  gcsPath: string;
}) {
  const key = menuKey(photo);
  const cached = menuLoads.get(key);
  if (cached) return cached.file;
  if (inFlightMenuLoad) {
    inFlightMenuLoad.controller.abort();
    dropMenuLoad(inFlightMenuLoad.key);
  }
  const controller = new AbortController();
  const file: Promise<File | null> = loadPhotoFile(photo, {
    priority: true,
    signal: controller.signal
  }).then(loaded => {
    if (inFlightMenuLoad?.controller === controller) inFlightMenuLoad = null;
    const entry = menuLoads.get(key);
    if (entry?.file === file) {
      // A failed load isn't kept, so Retry loads it again.
      if (loaded) entry.loaded = loaded;else dropMenuLoad(key);
    }
    return loaded;
  });
  inFlightMenuLoad = {
    key,
    controller
  };
  menuLoads.set(key, {
    file,
    expire: setTimeout(() => {
      if (menuLoads.get(key)?.file === file) menuLoads.delete(key);
    }, MENU_CACHE_MS)
  });
  while (menuLoads.size > MENU_CACHE_SIZE) {
    const oldest = menuLoads.keys().next().value;
    if (oldest === undefined) break;
    dropMenuLoad(oldest);
  }
  return file;
}
type MenuPhoto = {
  status: "preparing";
} | {
  status: "ready";
  file: File;
} | {
  status: "failed";
};

/**
 * A photo menu's Download. On iPhone and iPad it opens the share sheet, where
 * "Save Image" puts the photo in Photos and "Save to Files" keeps the old
 * behaviour; everywhere else it downloads the file as before.
 *
 * Safari only opens the sheet straight after a tap, so the photo loads as
 * soon as the menu opens (this item mounts with it) and Download stays
 * disabled, as "Preparing…", until it is ready. The tap then shares the
 * loaded file with nothing awaited first. If the photo can't load, the item
 * offers Retry instead.
 */
export function DownloadMenuItem(t0) {
  const $ = _c(31);
  const {
    photo,
    onDownload,
    disabled
  } = t0;
  let t1;
  if ($[0] !== photo) {
    t1 = () => !!photo?.gcsPath && !isVideoItem(photo) && canSaveToPhotos();
    $[0] = photo;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [canShare] = useState(t1);
  const id = photo?.id;
  const gcsPath = photo?.gcsPath;
  const name = photo?.name;
  const [attempt, setAttempt] = useState(0);
  let t2;
  if ($[2] !== photo) {
    t2 = () => {
      const loaded = photo && menuLoads.get(menuKey(photo))?.loaded;
      return loaded ? {
        status: "ready",
        file: loaded
      } : {
        status: "preparing"
      };
    };
    $[2] = photo;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [state, setState] = useState(t2);
  let t3;
  if ($[4] !== canShare || $[5] !== gcsPath || $[6] !== id || $[7] !== name) {
    t3 = () => {
      if (!canShare || !id || !gcsPath) {
        return;
      }
      let current = true;
      loadMenuPhoto({
        id,
        gcsPath,
        name
      }).then(file => {
        if (current) {
          setState(file ? {
            status: "ready",
            file
          } : {
            status: "failed"
          });
        }
      });
      return () => {
        current = false;
      };
    };
    $[4] = canShare;
    $[5] = gcsPath;
    $[6] = id;
    $[7] = name;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== attempt || $[10] !== canShare || $[11] !== gcsPath || $[12] !== id || $[13] !== name) {
    t4 = [canShare, id, gcsPath, name, attempt];
    $[9] = attempt;
    $[10] = canShare;
    $[11] = gcsPath;
    $[12] = id;
    $[13] = name;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  useEffect(t3, t4);
  if (!canShare) {
    let t5;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <FiDownload className="h-4 w-4" />;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== disabled || $[17] !== onDownload) {
      t6 = <DropdownMenuItem onClick={onDownload} disabled={disabled} icon={t5}>Download</DropdownMenuItem>;
      $[16] = disabled;
      $[17] = onDownload;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    return t6;
  }
  if (state.status === "failed") {
    let t5;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = event => {
        event.preventDefault();
        setState({
          status: "preparing"
        });
        setAttempt(_temp);
      };
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    let t6;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <FiRefreshCw className="h-4 w-4" />;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    let t7;
    if ($[21] !== disabled) {
      t7 = <DropdownMenuItem onSelect={t5} disabled={disabled} icon={t6} hint="Couldn't load">Retry</DropdownMenuItem>;
      $[21] = disabled;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    return t7;
  }
  let t5;
  if ($[23] !== state.file || $[24] !== state.status) {
    t5 = () => {
      if (state.status === "ready") {
        sharePhotoFiles([state.file]);
      }
    };
    $[23] = state.file;
    $[24] = state.status;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const t6 = disabled || state.status === "preparing";
  let t7;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FiDownload className="h-4 w-4" />;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const t8 = state.status === "preparing" ? "Preparing\u2026" : "Download";
  let t9;
  if ($[27] !== t5 || $[28] !== t6 || $[29] !== t8) {
    t9 = <DropdownMenuItem onClick={t5} disabled={t6} icon={t7}>{t8}</DropdownMenuItem>;
    $[27] = t5;
    $[28] = t6;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
function _temp(n) {
  return n + 1;
}